import AppModal from '@/components/AppModal';
import AppPageTitle from '@/components/AppPageTitle';
import AppTable from '@/components/AppTable';
import AppTableQuery from '@/components/AppTableQuery';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';
import { getUserKeys } from '@/services/customer_care.service';
import { columnSorter, formatDate, formatNumber } from '@/utils/formatters';
import { getActiveBadge } from '@/utils/getActiveBadge';
import { EyeOutlined } from '@ant-design/icons';
import { Button, Space, Table } from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';

const CURRENT_MONTH = dayjs().format('YYYY-MM');

const toQueryMonth = (month) => (month ? dayjs(month, 'YYYY-MM').format('MM-YYYY') : null);

const getNearestKeyExpiry = (detail) => {
  if (!detail?.length) return null;

  const activeExpiries = detail
    .filter((item) => item.is_active && item.key_expired_time)
    .map((item) => item.key_expired_time)
    .sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());

  return activeExpiries[0] || null;
};

const keyHistoryColumns = [
  {
    title: 'Receive Date',
    dataIndex: 'key_start_time',
    key: 'key_start_time',
    render: (key_start_time) => (key_start_time ? formatDate(key_start_time) : '-'),
  },
  {
    title: 'Expiry Date',
    dataIndex: 'key_expired_time',
    key: 'key_expired_time',
    render: (key_expired_time) => (key_expired_time ? formatDate(key_expired_time) : '-'),
  },
  {
    title: 'Number of keys',
    dataIndex: 'total_key',
    key: 'total_key',
    render: (total_key) => (total_key ? formatNumber(total_key) : '0'),
  },
  {
    title: 'Status',
    dataIndex: 'is_active',
    key: 'is_active',
    render: (is_active) =>
      getActiveBadge(!!is_active, {
        activeText: 'Valid',
        inactiveText: 'Expired',
      }),
  },
];

function UserKeyManagement() {
  const [searchValue, setSearchValue] = useState(null);
  const [month, setMonth] = useState(CURRENT_MONTH);
  const [query, setQuery] = useState({
    isdn: null,
    month: toQueryMonth(CURRENT_MONTH),
  });
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(1);
  const [keyHistoryRecord, setKeyHistoryRecord] = useState(null);
  const isBreakpoint = useMediaQuery(`(max-width: ${mediaQueryPoints.mdPlus}px)`);

  const columns = [
    {
      title: 'ISDN',
      dataIndex: 'isdn',
      key: 'isdn',
      fixed: isBreakpoint ? 'left' : '',
      sorter: columnSorter('isdn'),
      render: (isdn) => (isdn ? isdn : '-'),
    },
    {
      title: 'Extra Pieces',
      dataIndex: 'number_duplicate',
      key: 'number_duplicate',
      sorter: columnSorter('number_duplicate'),
      render: (number_duplicate) => (number_duplicate ? formatNumber(number_duplicate) : '0'),
    },
    {
      title: 'Valid Keys',
      dataIndex: 'total_key',
      key: 'total_key',
      sorter: columnSorter('total_key'),
      render: (total_key) => (total_key ? formatNumber(total_key) : '0'),
    },
    {
      title: 'Max Box Eligible',
      dataIndex: 'milestone',
      key: 'milestone',
      render: (milestone) => (milestone ? formatNumber(milestone) : '0'),
    },
    {
      title: 'Nearest Key Expiry',
      dataIndex: 'detail',
      key: 'nearest_key_expiry',
      render: (detail) => {
        const nearestExpiry = getNearestKeyExpiry(detail);
        return nearestExpiry ? formatDate(nearestExpiry, 'DD/MM/YYYY') : '-';
      },
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => setKeyHistoryRecord(record)}
          icon={isBreakpoint ? <EyeOutlined /> : null}
        >
          {isBreakpoint ? null : 'View'}
        </Button>
      ),
    },
  ];

  const { data, isLoading, refetch, isRefetching } = usePaginatedQuery(
    () =>
      getUserKeys({
        ...query,
        pageSize: pageSize,
        offset: pageIndex,
      }),
    ['user-key-management', query, pageSize, pageIndex]
  );

  const handleQuery = () => {
    setQuery({
      isdn: searchValue,
      month: toQueryMonth(month),
    });
    setPageIndex(1);
  };

  const handleReset = () => {
    setSearchValue(null);
    setMonth(CURRENT_MONTH);
    setQuery({
      isdn: null,
      month: toQueryMonth(CURRENT_MONTH),
    });
    setPageIndex(1);
  };

  return (
    <Space direction="vertical" className="w-full">
      <AppPageTitle title="User Key Management" />

      <AppTableQuery
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        searchPlaceholder="Search by MSISDN"
        hasFilter={false}
        hasDatePicker
        picker="month"
        time={month}
        setTime={setMonth}
        timeLabel="Month"
        timePlaceholder="Month"
        handleQuery={handleQuery}
        handleReset={handleReset}
      />

      <AppTable
        columns={columns}
        dataSource={data?.data}
        totalItems={data?.total}
        loading={isLoading || isRefetching}
        onRefresh={refetch}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={(page, size) => {
          setPageIndex(page);
        }}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
        }}
      />

      <AppModal
        title="Key History"
        open={!!keyHistoryRecord}
        onCancel={() => setKeyHistoryRecord(null)}
        footer={null}
        width={600}
      >
        <Table
          columns={keyHistoryColumns}
          dataSource={keyHistoryRecord?.detail || []}
          pagination={false}
          rowKey={(record, index) => index}
          size="small"
        />
      </AppModal>
    </Space>
  );
}

export default UserKeyManagement;
